import { createContext, useContext, useState, ReactNode } from "react";

type Lang = "ar" | "en";

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const translations: Record<Lang, Record<string, string>> = {
  ar: {
    // Navbar
    "nav.home": "الرئيسية",
    "nav.projects": "مشاريعنا",
    "nav.about": "من نحن",
    "nav.features": "المميزات",
    "nav.contact": "تواصل معنا",
    "nav.blog": "المدونة",
    "nav.call": "اتصل بنا",

    // Hero
    "hero.badge": "شركة صحار العقارية",
    "hero.title1": "حياة اس\nنصنع لك\nتثنائية",
    "hero.title2": "",
    "hero.subtitle": "نقدم مشاريع سكنية فاخرة بتصاميم عصرية ومواقع مميزة.",
    "hero.cta1": "استكشف مشاريعنا",
    "hero.cta2": "تواصل معنا",

    // Who We Are
    "whowe.title": "من نحن",
    "whowe.subtitle": "نحلم ونحقق",
    "whowe.desc": "تأسست شركة صحار العقارية عام 2020م بقيادة طموحة وخبرات واسعة بهدف تقديم منتجات مميزة بما يتناسب مع مستقبل السوق العقارية في المملكة العربية السعودية",
    "whowe.mission.label": "رسالتنا",
    "whowe.mission.text": "تقديم منتجات عقارية ذات جودة عالية، تساهم في نمو اقتصاد السوق العقاري السعودي، وتساهم في تلبية احتياجات العملاء ،و تحقيق عوائد مجزية للمستثمرين",
    "whowe.tagline1": "تصميم يليق بك",
    "whowe.tagline2": "تصاميم تنبض بالرقي والهدوء",

    // Projects
    "projects.label": "أعمالنا المميزة",
    "projects.title": "مشاريعنا",
    "projects.viewDetails": "عرض التفاصيل",
    "projects.comingSoon": "قيد الإنشاء",
    "projects.p1.title": "فلل النرجس",
    "projects.p1.desc": "أحدى مشاريع صحار العقارية السكنية 6 فلل سكنية فاخرة بمساحات مختلفة 207-230 م²",
    "projects.p1.status": "تم البيع",
    "projects.p1.units": "6 وحدات",
    "projects.p2.title": "فلل القيروان",
    "projects.p2.desc": "فلل صحار العقارية في حي القيروان بتصاميم عصرية فاخرة بمسطحات بناء 483-570 م²",
    "projects.p2.status": "تم البيع",
    "projects.p2.units": "نموذجين",
    "projects.p3.title": "أدوار النرجس",
    "projects.p3.desc": "27 دور سكني فاخر بمساحات متنوعة صممت بعناية فائقة",
    "projects.p3.status": "متاح للبيع",
    "projects.p3.units": "27 وحدة",
    "projects.p4.title": "أدوار العقيق",
    "projects.p4.desc": "9 أدوار سكنية فاخرة في حي العقيق بالرياض، تصميم عصري ومداخل مستقلة وتشطيبات راقية",
    "projects.p4.status": "متاح للبيع",
    "projects.p4.units": "9 أدوار",
    "projects.p5.title": "فلل الملقا",
    "projects.p5.desc": "فلل سكنية فاخرة في حي الملقا بالرياض بتصاميم معمارية عصرية ومداخل مستقلة",
    "projects.p5.status": "قريباً",
    "projects.p5.units": "فلل فاخرة",
    "projects.p6.title": "أدوار النخيل",
    "projects.p6.desc": "أدوار سكنية فاخرة في حي النخيل بالرياض بتصاميم عصرية ومداخل مستقلة",
    "projects.p6.status": "قريباً",
    "projects.p6.units": "أدوار فاخرة",

    // About
    "about.label": "تعرّف علينا",
    "about.title": "تعرف",
    "about.titleHighlight": "علينا",
    "about.p1": "تأسست شركة صحار العقارية عام 2020م بقيادة طموحة وخبرات واسعة بهدف تقديم منتجات مميزة بما يتناسب مع مستقبل السوق العقارية في المملكة العربية السعودية.",
    "about.p2": "نؤمن بأن أمان الإنسان وسعادته في مسكنه تنطلق من شعور داخلي بالراحة والطمأنينة. لذلك نحرص على تقديم مشاريع سكنية تجمع بين الرحابة والحيوية والاستقرار.",
    "about.stat1": "وحدة سكنية منجزة",
    "about.stat2": "عميل سعيد",
    "about.stat3": "سنوات خبرة",
    "about.stat4": "جودة عالية",
    "about.badge": "سنوات من التميز",

    // Features
    "features.label": "لماذا نحن",
    "features.title": "مميزات مشاريعنا",
    "features.f1.title": "جودة البناء",
    "features.f1.desc": "نستخدم أفضل المواد والتقنيات لضمان جودة بناء استثنائية تدوم طويلاً",
    "features.f2.title": "مواقع مميزة",
    "features.f2.desc": "نختار أفضل المواقع الاستراتيجية في قلب الرياض بالقرب من الخدمات",
    "features.f3.title": "خدمات متكاملة",
    "features.f3.desc": "نقدم خدمات شاملة من التصميم إلى التسليم مع ضمان ما بعد البيع",
    "features.f4.title": "تصاميم عصرية",
    "features.f4.desc": "تصاميم معمارية حديثة تجمع بين الأصالة والمعاصرة بلمسات فنية",
    "features.f5.title": "تكييف مركزي",
    "features.f5.desc": "أنظمة تكييف مخفية متطورة لراحة مثالية في جميع الفصول",
    "features.f6.title": "تشطيبات فاخرة",
    "features.f6.desc": "أرضيات بورسلان إسباني وألمنيوم عالي الجودة وتشطيبات متميزة",

    // Testimonials
    "testimonials.label": "آراء عملائنا",
    "testimonials.title": "ماذا يقول عملاؤنا",
    "testimonials.r1.name": "عبدالله الشمري",
    "testimonials.r1.role": "مالك فيلا في النرجس",
    "testimonials.r1.text": "تجربة ممتازة من البداية للنهاية، جودة البناء والتشطيبات فاقت توقعاتي. أنصح الجميع بالتعامل مع صحار.",
    "testimonials.r2.name": "فهد العتيبي",
    "testimonials.r2.role": "مالك فيلا في القيروان",
    "testimonials.r2.text": "التصميم العصري والمساحات المدروسة خلتني أحس إن البيت مصمم خصيصاً لعائلتي. شكراً صحار.",
    "testimonials.r3.name": "سعود القحطاني",
    "testimonials.r3.role": "مالك دور سكني",
    "testimonials.r3.text": "الالتزام بالمواعيد والشفافية في التعامل من أهم الأشياء اللي لاحظتها. تجربة احترافية بكل المقاييس.",
    "testimonials.r4.name": "محمد الدوسري",
    "testimonials.r4.role": "مستثمر عقاري",
    "testimonials.r4.text": "استثمرت في مشاريع صحار وكانت العوائد ممتازة. جودة المنتج العقاري تضمن قيمة مستدامة.",

    // Contact
    "contact.label": "نسعد بتواصلكم",
    "contact.title": "تواصل معنا",
    "contact.desc": "شركة صحار العقارية تسعد بتواصلكم للاستفسار عن مشاريعنا السكنية الفاخرة في الرياض. فريقنا جاهز لخدمتكم.",
    "contact.phone": "اتصل بنا",
    "contact.email": "البريد الإلكتروني",
    "contact.location": "الموقع",
    "contact.locationValue": "القيروان، الرياض، المملكة العربية السعودية",
    "contact.whatsapp": "تواصل عبر واتساب",
    "contact.formName": "الاسم الكامل",
    "contact.formNamePlaceholder": "أدخل اسمك",
    "contact.formPhone": "رقم الجوال",
    "contact.formMessage": "الرسالة",
    "contact.formMessagePlaceholder": "اكتب رسالتك هنا...",
    "contact.formProject": "المشروع",
    "contact.formProjectPlaceholder": "اختر المشروع",
    "contact.submit": "إرسال الرسالة",
    "contact.submitted": "تم الإرسال بنجاح ✓",

    // Thank You
    "thankYou.title": "شكراً لتواصلكم معنا",
    "thankYou.desc": "تم استلام طلبكم بنجاح. سيقوم فريق صحار العقارية بالتواصل معكم في أقرب وقت.",
    "thankYou.backHome": "العودة للرئيسية",
    "thankYou.callUs": "اتصل بنا",

    // Footer
    "footer.desc": "شركة صحار العقارية - نصنع لك حياة استثنائية بمشاريع سكنية فاخرة في الرياض",
    "footer.quickLinks": "روابط سريعة",
    "footer.contactUs": "تواصل معنا",
    "footer.rights": "© 2026 شركة صحار العقارية. جميع الحقوق محفوظة.",

    // Blog
    "blog.label": "مدونة صحار",
    "blog.title": "آخر الأخبار والمقالات",
    "blog.readMore": "اقرأ المزيد",
    "blog.1.title": "رؤية 2030 والسوق العقاري السعودي",
    "blog.1.excerpt": "كيف تؤثر رؤية المملكة 2030 على قطاع العقارات والفرص الاستثمارية في الرياض والمدن الكبرى",
    "blog.1.date": "10 أبريل 2026",
    "blog.1.category": "رؤية 2030",
    "blog.2.title": "دليلك الشامل لحي النرجس في الرياض",
    "blog.2.excerpt": "اكتشف لماذا يعد حي النرجس من أفضل الأحياء السكنية في شمال الرياض وما يميزه من خدمات ومرافق",
    "blog.2.date": "5 أبريل 2026",
    "blog.2.category": "أحياء الرياض",
    "blog.3.title": "نصائح عند شراء فيلا سكنية جديدة",
    "blog.3.excerpt": "أهم النقاط التي يجب مراعاتها قبل شراء فيلا سكنية من حيث الموقع والتصميم وجودة البناء",
    "blog.3.date": "28 مارس 2026",
    "blog.3.category": "نصائح عقارية",

    // FAQ
    "faq.label": "الأسئلة الشائعة",
    "faq.title": "أسئلة متكررة",
    "faq.1.q": "ما هي طرق الدفع والتمويل المتاحة؟",
    "faq.1.a": "نوفر خيارات دفع مرنة تشمل الدفع النقدي، والتقسيط المباشر، بالإضافة إلى التمويل العقاري بالتعاون مع البنوك المعتمدة في المملكة. يمكن لفريقنا مساعدتك في اختيار الخطة الأنسب لك.",
    "faq.2.q": "أين تقع مشاريع صحار العقارية؟",
    "faq.2.a": "تتمركز مشاريعنا في أرقى أحياء الرياض مثل حي النرجس وحي القيروان، وهي مواقع استراتيجية قريبة من الخدمات الأساسية والطرق الرئيسية.",
    "faq.3.q": "ما هي مواعيد التسليم المتوقعة؟",
    "faq.3.a": "نلتزم بجداول تسليم واضحة ومحددة لكل مشروع. يتم إبلاغ العميل بموعد التسليم المتوقع عند التعاقد، ونحرص على الالتزام بالمواعيد المتفق عليها.",
    "faq.4.q": "هل تقدمون ضمانات على الوحدات السكنية؟",
    "faq.4.a": "نعم، نقدم ضمانات شاملة تغطي العزل المائي والحراري، تمديدات السباكة والكهرباء، الأدوات الصحية، الأعمال الخشبية، والمصاعد. تفاصيل الضمان تُحدد في عقد البيع.",
    "faq.5.q": "هل يمكنني زيارة المشاريع قبل الشراء؟",
    "faq.5.a": "بالتأكيد! نرحب بزيارتكم لمشاريعنا في أي وقت. يمكنك التواصل معنا لتحديد موعد مناسب وسيرافقك أحد ممثلينا في جولة شاملة.",
    "faq.6.q": "هل الوحدات مناسبة للاستثمار؟",
    "faq.6.a": "نعم، مشاريعنا تقع في مواقع ذات طلب عالٍ مما يضمن عوائد استثمارية مجزية سواء من خلال التأجير أو إعادة البيع. كثير من عملائنا مستثمرون حققوا عوائد ممتازة.",


    "common.backHome": "العودة للرئيسية",
    "common.sohar": "\n",
    "common.callUs": "اتصل بنا",
    "common.whatsapp": "واتساب",
    "common.luxuryProject": "مشروع سكني فاخر",
    "common.homeComfort": "بيتك.. أصل راحتك",
    "common.footerRights": "جميع الحقوق محفوظة © 2026 صحار للتطوير العقاري",
    "common.privacy": "سياسة الخصوصية",
    "common.terms": "الشروط والأحكام",

    // Adwar Project
    "adwar.title": "أدوار النرجس",
    "adwar.subtitle": "27 دور سكني فاخر بمساحات متنوعة في حي النرجس — بيتك.. أصل راحتك",
    "adwar.available": "متاح للبيع",
    "adwar.unitsInfo": "9 فلل • 27 دور منفصل • 3 نماذج",
    "adwar.stat1": "دور منفصل",
    "adwar.stat2": "فلل سكنية",
    "adwar.stat3": "نماذج سكنية",
    "adwar.stat4": "م² المساحات",
    "adwar.aboutLabel": "عن المشروع",
    "adwar.aboutTitle": "فخامة تستحقها",
    "adwar.aboutP1": "يتكون مشروع أدوار النرجس من 27 دوراً سكنياً منفصلاً، ويتميز كل دور بتصميم معماري فريد بهوية بصرية واحدة، ومدخل خاص، وموقف سيارات مخصص. كما يحتوي على عدادات كهرباء وماء منفصلة، مما يوفر خصوصية كاملة للسكان.",
    "adwar.aboutP2": "تم تصميم الوحدات السكنية بعناية فائقة لتمزج بين الأناقة العصرية والوظائف العملية. استخدام مواد فاخرة مثل الرخام والخشب الطبيعي وألوان هادئة يخلق جواً دافئاً يعكس أسلوب حياة راقياً.",
    "adwar.locationLabel": "الموقع الاستراتيجي",
    "adwar.locationTitle": "حي النرجس — قلب الرياض",
    "adwar.featuresLabel": "مميزات المشروع",
    "adwar.featuresTitle": "تفاصيل تصنع الفرق",
    "adwar.floorLabel": "نماذج الوحدات",
    "adwar.floorTitle": "مخططات الأدوار",
    "adwar.guaranteesLabel": "ضمانات المشروع",
    "adwar.guaranteesTitle": "الضمانات",
    "adwar.designLabel": "التصميم الداخلي",
    "adwar.designTitle": "تصاميم متقنة",
    "adwar.designDesc": "تم تصميم الوحدات السكنية بعناية فائقة لتمزج بين الأناقة العصرية والوظائف العملية. استخدام مواد فاخرة مثل الرخام والخشب الطبيعي، وألوان هادئة تخلق جواً دافئاً يعكس أسلوب حياة راقياً.",
    "adwar.galleryLabel": "معرض الصور",
    "adwar.galleryTitle": "لمحات من المشروع",
    "adwar.ctaDesc": "تواصل معنا الآن لحجز وحدتك في مشروع أدوار النرجس",
    "adwar.feat.electricity": "كهرباء مستقلة",
    "adwar.feat.water": "عدادات مياه منفصلة",
    "adwar.feat.security": "كاميرات أمنية",
    "adwar.feat.ac": "تكييف مركزي مخفي",
    "adwar.feat.soundproof": "عزل صوتي بين الأدوار",
    "adwar.feat.parking": "مواقف سيارات خاصة",
    "adwar.feat.entrance": "مداخل منفصلة",
    "adwar.feat.finishes": "تشطيبات فاخرة",
    "adwar.guarantee.waterproof": "العزل المائي والحراري",
    "adwar.guarantee.plumbing": "تمديدات السباكة",
    "adwar.guarantee.electrical": "تمديدات الكهرباء",
    "adwar.guarantee.sanitary": "الأدوات الصحية",
    "adwar.guarantee.woodwork": "الأعمال الخشبية",
    "adwar.guarantee.meters": "العدادات",
    "adwar.guarantee.elevators": "المصاعد",
    "adwar.floor.ground": "الدور الأرضي",
    "adwar.floor.first": "الدور الأول",
    "adwar.floor.second": "الدور الثاني",
    "adwar.dist.kingdom": "مستشفى المملكة",
    "adwar.dist.kingdom.time": "3 دقائق",
    "adwar.dist.sabic": "محطة قطار سابك",
    "adwar.dist.sabic.time": "7 دقائق",
    "adwar.dist.park": "حديقة ومسجد الحي",
    "adwar.dist.park.time": "2 دقائق",
    "adwar.dist.imam": "جامعة الإمام",
    "adwar.dist.imam.time": "5 دقائق",
    "adwar.dist.stadium": "ملعب الملك سلمان",
    "adwar.dist.stadium.time": "5 دقائق",
    "adwar.dist.expo": "إكسبو 2030",
    "adwar.dist.expo.time": "8 دقائق",
    "adwar.dist.airport": "مطار الملك خالد",
    "adwar.dist.airport.time": "15 دقيقة",
    "adwar.dist.roshn": "واجهة روشن",
    "adwar.dist.roshn.time": "10 دقائق",

    // Narjis Project
    "narjis.title": "فلل النرجس",
    "narjis.subtitle": "أحدى مشاريع صحار العقارية السكنية — 6 فلل سكنية فاخرة بمساحات مختلفة 207-230 م²",
    "narjis.available": "متاح للبيع",
    "narjis.unitsInfo": "6 فلل • مساحات 207-230 م²",
    "narjis.stat1": "فلل سكنية فاخرة",
    "narjis.stat2": "نماذج مختلفة",
    "narjis.stat3": "م² المساحات",
    "narjis.stat4": "أدوار لكل فيلا",
    "narjis.aboutLabel": "عن المشروع",
    "narjis.aboutTitle": "فلل النرجس",
    "narjis.aboutP1": "أحدى مشاريع صحار العقارية السكنية، تتكون من 6 فلل سكنية فاخرة بمساحات مختلفة تتراوح بين 207 إلى 230 م²، بمواصفات متنوعة تضمن تلبية احتياجات ومتطلبات كافة العملاء.",
    "narjis.aboutP2": "نؤمن في صحار بأن أمان الإنسان وسعادته في مسكنه، تنطلق من شعور داخلي بالراحة والطمأنينة عند رؤيته للخيارات السكنية ومدى توافقها مع رغباته وتطلعاته.",
    "narjis.featuresLabel": "مواصفات المشروع",
    "narjis.featuresTitle": "مواصفات الفلل",
    "narjis.feat.ac": "تكييف مخفي",
    "narjis.feat.insulation": "عزل بجودة عالية",
    "narjis.feat.meters": "عداد مياه وكهرباء مستقل",
    "narjis.feat.elevator": "تأسيس مصعد",
    "narjis.feat.tanks": "خزانات مياه",
    "narjis.modelsLabel": "تفاصيل الفلل",
    "narjis.modelsTitle": "3 نماذج سكنية",
    "narjis.model": "نموذج",
    "narjis.groundFloor": "الطابق الأرضي",
    "narjis.firstFloor": "الطابق الأول",
    "narjis.secondFloor": "الطابق الثاني",
    "narjis.locationLabel": "موقع المشروع",
    "narjis.locationTitle": "حي النرجس — الرياض",
    "narjis.nearby.hospitals": "مستشفيات",
    "narjis.nearby.malls": "مجمعات تجارية",
    "narjis.nearby.universities": "جامعات دراسية",
    "narjis.nearby.gyms": "نوادي رياضية",
    "narjis.roadsNote": "تبعد مدة لا تزيد عن 3 دقائق من",
    "narjis.road1": "طريق أبو بكر الصديق",
    "narjis.road2": "طريق عثمان بن عفان",
    "narjis.road3": "طريق الثمامة",
    "narjis.designLabel": "التصميم المعماري",
    "narjis.designTitle": "تصاميم عصرية فاخرة",
    "narjis.designP1": "تتميز فلل النرجس بتصاميم معمارية عصرية تجمع بين الأناقة والعملية. مساحات واسعة ومفتوحة مع استخدام مواد بناء عالية الجودة وتشطيبات فاخرة تضمن بيئة سكنية مريحة ومميزة.",
    "narjis.designP2": "كل فيلا مصممة بعناية لتوفر أقصى درجات الخصوصية والراحة، مع مراعاة التهوية الطبيعية والإضاءة المثالية في جميع الغرف.",
    "narjis.ctaDesc": "تواصل معنا الآن لحجز فيلتك",

    // Qairawan Project
    "qairawan.title": "فلل القيروان",
    "qairawan.subtitle": "فلل صحار العقارية في حي القيروان — بمساحات بناء تصل إلى 570 م² بتصاميم عصرية فاخرة",
    "qairawan.status": "قريباً",
    "qairawan.unitsInfo": "نموذجين • مساحات 483-570 م²",
    "qairawan.stat1": "نماذج سكنية",
    "qairawan.stat2": "أدوار لكل فيلا",
    "qairawan.stat3": "م² مسطحات البناء",
    "qairawan.stat4.value": "القيروان",
    "qairawan.stat4": "حي الموقع",
    "qairawan.aboutLabel": "عن المشروع",
    "qairawan.aboutTitle": "فلل القيروان",
    "qairawan.aboutP1": "تقع فلل صحار العقارية في حي القيروان بمساحة 280 متر مربع على شارع عريض 20 متر، وقد تم التعاون مع المعماري منصور جنوبي لتقديم تصاميم استثنائية تجمع بين الفخامة والعملية.",
    "qairawan.aboutP2": "المشروع يقع بالقرب من طريق الملك سلمان في مخطط الدار، بجانب أكبر داون تاون حديث في العالم، مما يجعله موقعاً استثمارياً مميزاً.",
    "qairawan.featuresLabel": "تجربة سكنية مريحة ومتكاملة",
    "qairawan.featuresTitle": "مواصفات الفلل",
    "qairawan.feat.ac": "تكييف مخفي لكامل الفيلا",
    "qairawan.feat.electricity": "كهرباء معلقة",
    "qairawan.feat.aluminum": "ألمنيوم دريتمال",
    "qairawan.feat.plumbing": "سباكة تغذية معلقة",
    "qairawan.feat.flooring": "أرضيات بورسلان إسباني",
    "qairawan.feat.elevator": "تأسيسات متكاملة للمصاعد",
    "qairawan.interiorLabel": "التصميم المعماري",
    "qairawan.interiorTitle": "تصاميم عصرية فاخرة",
    "qairawan.interiorP1": "بالتعاون مع المعماري منصور جنوبي ومكتب أنوس للتصميم المعماري، تم تصميم الفلل بأسلوب عصري يجمع بين الراحة والأناقة مع مراعاة أدق التفاصيل.",
    "qairawan.interiorP2": "نصنع لك تجربة سكنية مريحة ومتكاملة بأعلى المعايير والمواصفات.",
    "qairawan.modelsLabel": "تفاصيل الفلل",
    "qairawan.modelsTitle": "نموذجين سكنيين",
    "qairawan.buildArea": "مسطحات البناء",
    "qairawan.modelA": "نموذج A",
    "qairawan.modelB": "نموذج B",
    "qairawan.groundFloor": "الدور الأرضي",
    "qairawan.firstFloor": "الدور الأول",
    "qairawan.secondFloor": "الدور الثاني (الملحق)",
    "qairawan.room.majlis": "مجلس",
    "qairawan.room.dining": "صالة طعام",
    "qairawan.room.familyHall": "صالة عائلية",
    "qairawan.room.library": "مساحة مكتبة",
    "qairawan.room.familyLiving": "غرفة معيشة عائلية",
    "qairawan.room.laundry": "غرفة غسيل",
    "qairawan.room.maid": "غرفة عاملة",
    "qairawan.room.parking": "موقف سيارة",
    "qairawan.room.kitchen": "مطبخ",
    "qairawan.room.storage": "مستودع",
    "qairawan.room.courtyard": "فناء داخلي",
    "qairawan.room.outdoor": "جلسة خارجية",
    "qairawan.room.master": "غرفة رئيسية",
    "qairawan.room.masterSuite": "غرفة ماستر",
    "qairawan.room.bedrooms": "غرفتين نوم",
    "qairawan.room.balcony": "بلكونة",
    "qairawan.locationLabel": "موقع المشروع",
    "qairawan.locationTitle": "حي القيروان — الرياض",
    "qairawan.nearby.downtown": "بجانب أكبر داون تاون حديث في العالم",
    "qairawan.nearby.malls": "مجمعات تجارية",
    "qairawan.nearby.schools": "جامعات ومدارس",
    "qairawan.nearby.gyms": "نوادي رياضية",
    "qairawan.locationNote": "الموقع",
    "qairawan.locationDesc": "حي القيروان، بالقرب من طريق الملك سلمان — مخطط الدار — الرياض",
    "qairawan.ctaDesc": "تواصل معنا الآن للاستفسار عن المشروع",
  },
  en: {
    // Navbar
    "nav.home": "Home",
    "nav.projects": "Projects",
    "nav.about": "About Us",
    "nav.features": "Features",
    "nav.contact": "Contact",
    "nav.blog": "Blog",
    "nav.call": "Call Us",

    // Hero
    "hero.badge": "Sohar Real Estate",
    "hero.title1": "We Craft\nExceptional\nLiving",
    "hero.title2": "",
    "hero.subtitle": "Premium residential projects with modern designs in prime locations.",
    "hero.cta1": "Explore Projects",
    "hero.cta2": "Contact Us",

    // Who We Are
    "whowe.title": "Who We Are",
    "whowe.subtitle": "We Dream & Deliver",
    "whowe.desc": "Sohar Real Estate was founded in 2020 with ambitious leadership and extensive expertise, aiming to deliver distinguished products aligned with the future of the Saudi Arabian real estate market.",
    "whowe.mission.label": "Our Mission",
    "whowe.mission.text": "Delivering high-quality real estate products that contribute to the growth of the Saudi real estate market economy, meeting customer needs, and achieving rewarding returns for investors",
    "whowe.tagline1": "A Design That Suits You",
    "whowe.tagline2": "Designs That Pulse with Elegance & Calm",

    // Projects
    "projects.label": "Our Distinguished Work",
    "projects.title": "Our Projects",
    "projects.viewDetails": "View Details",
    "projects.comingSoon": "Coming Soon",
    "projects.p1.title": "Al Narjis Villas",
    "projects.p1.desc": "One of Sohar's residential projects — 6 luxury villas with areas of 207-230 m²",
    "projects.p1.status": "Sold Out",
    "projects.p1.units": "6 Units",
    "projects.p2.title": "Al Qairawan Villas",
    "projects.p2.desc": "Sohar villas in Al Qairawan with modern luxury designs — building areas of 483-570 m²",
    "projects.p2.status": "Sold Out",
    "projects.p2.units": "2 Models",
    "projects.p3.title": "Al Narjis Floors",
    "projects.p3.desc": "27 luxury residential floors of various sizes, meticulously designed",
    "projects.p3.status": "Available",
    "projects.p3.units": "27 Units",
    "projects.p4.title": "Al Aqiq Floors",
    "projects.p4.desc": "9 luxury residential floors in Riyadh's Al-Aqiq district, with modern design, private entrances, and premium finishes",
    "projects.p4.status": "Available",
    "projects.p4.units": "9 Floors",
    "projects.p5.title": "Al Malqa Villas",
    "projects.p5.desc": "Luxury villas in Riyadh's Al-Malqa district with modern architecture and private entrances",
    "projects.p5.status": "Coming Soon",
    "projects.p5.units": "Luxury Villas",
    "projects.p6.title": "Al Nakhil Floors",
    "projects.p6.desc": "Luxury residential floors in Riyadh's Al-Nakhil district with modern designs and private entrances",
    "projects.p6.status": "Coming Soon",
    "projects.p6.units": "Luxury Floors",

    // About
    "about.label": "Get to Know Us",
    "about.title": "Get to Know",
    "about.titleHighlight": "Us",
    "about.p1": "Sohar Real Estate was founded in 2020 with ambitious leadership and extensive expertise, aiming to deliver distinguished products aligned with the future of the Saudi Arabian real estate market.",
    "about.p2": "We believe that a person's sense of safety and happiness in their home begins with an inner feeling of comfort and tranquility. That's why we ensure our residential projects combine spaciousness, vitality, and stability.",
    "about.stat1": "Residential Units Completed",
    "about.stat2": "Happy Clients",
    "about.stat3": "Years of Experience",
    "about.stat4": "Premium Quality",
    "about.badge": "Years of Excellence",

    // Features
    "features.label": "Why Choose Us",
    "features.title": "Project Features",
    "features.f1.title": "Build Quality",
    "features.f1.desc": "We use the finest materials and techniques to ensure exceptional, long-lasting construction quality",
    "features.f2.title": "Prime Locations",
    "features.f2.desc": "We select the best strategic locations in the heart of Riyadh, close to essential services",
    "features.f3.title": "Complete Services",
    "features.f3.desc": "Comprehensive services from design to delivery with after-sales warranty",
    "features.f4.title": "Modern Designs",
    "features.f4.desc": "Contemporary architectural designs blending heritage and modernity with artistic touches",
    "features.f5.title": "Central AC",
    "features.f5.desc": "Advanced concealed air conditioning systems for optimal comfort in all seasons",
    "features.f6.title": "Luxury Finishes",
    "features.f6.desc": "Spanish porcelain flooring, premium aluminum, and distinguished finishes throughout",

    // Testimonials
    "testimonials.label": "Client Reviews",
    "testimonials.title": "What Our Clients Say",
    "testimonials.r1.name": "Abdullah Al-Shammari",
    "testimonials.r1.role": "Villa Owner in Al Narjis",
    "testimonials.r1.text": "An excellent experience from start to finish. The build quality and finishes exceeded my expectations. I recommend Sohar to everyone.",
    "testimonials.r2.name": "Fahad Al-Otaibi",
    "testimonials.r2.role": "Villa Owner in Al Qairawan",
    "testimonials.r2.text": "The modern design and well-planned spaces made me feel like the home was custom-built for my family. Thank you, Sohar.",
    "testimonials.r3.name": "Saud Al-Qahtani",
    "testimonials.r3.role": "Residential Floor Owner",
    "testimonials.r3.text": "Commitment to timelines and transparency in dealings were the standout qualities. A truly professional experience by all standards.",
    "testimonials.r4.name": "Mohammed Al-Dosari",
    "testimonials.r4.role": "Real Estate Investor",
    "testimonials.r4.text": "I invested in Sohar projects and the returns were excellent. The quality of the real estate product ensures sustainable value.",

    // Contact
    "contact.label": "We'd Love to Hear from You",
    "contact.title": "Contact Us",
    "contact.desc": "Sohar Real Estate is delighted to hear from you regarding our luxury residential projects in Riyadh. Our team is ready to serve you.",
    "contact.phone": "Call Us",
    "contact.email": "Email",
    "contact.location": "Location",
    "contact.locationValue": "Al Qairawan, Riyadh, Saudi Arabia",
    "contact.whatsapp": "Chat on WhatsApp",
    "contact.formName": "Full Name",
    "contact.formNamePlaceholder": "Enter your name",
    "contact.formPhone": "Phone Number",
    "contact.formMessage": "Message",
    "contact.formMessagePlaceholder": "Write your message here...",
    "contact.formProject": "Project",
    "contact.formProjectPlaceholder": "Select a project",
    "contact.submit": "Send Message",
    "contact.submitted": "Sent Successfully ✓",

    // Thank You
    "thankYou.title": "Thank You for Contacting Us",
    "thankYou.desc": "Your request has been received successfully. The Sohar Real Estate team will contact you as soon as possible.",
    "thankYou.backHome": "Back to Home",
    "thankYou.callUs": "Call Us",

    // Footer
    "footer.desc": "Sohar Real Estate — Crafting exceptional living through premium residential projects in Riyadh",
    "footer.quickLinks": "Quick Links",
    "footer.contactUs": "Contact Us",
    "footer.rights": "© 2026 Sohar Real Estate. All rights reserved.",

    // Blog
    "blog.label": "Sohar Blog",
    "blog.title": "Latest News & Articles",
    "blog.readMore": "Read More",
    "blog.1.title": "Vision 2030 & Saudi Real Estate Market",
    "blog.1.excerpt": "How Saudi Arabia's Vision 2030 is transforming the real estate sector and creating investment opportunities in Riyadh",
    "blog.1.date": "April 10, 2026",
    "blog.1.category": "Vision 2030",
    "blog.2.title": "Complete Guide to Al Narjis District in Riyadh",
    "blog.2.excerpt": "Discover why Al Narjis is one of the best residential districts in north Riyadh and what sets it apart",
    "blog.2.date": "April 5, 2026",
    "blog.2.category": "Riyadh Districts",
    "blog.3.title": "Tips for Buying a New Residential Villa",
    "blog.3.excerpt": "Key considerations before purchasing a residential villa regarding location, design, and build quality",
    "blog.3.date": "March 28, 2026",
    "blog.3.category": "Real Estate Tips",

    // FAQ
    "faq.label": "FAQ",
    "faq.title": "Frequently Asked Questions",
    "faq.1.q": "What payment and financing options are available?",
    "faq.1.a": "We offer flexible payment options including cash payment, direct installments, and mortgage financing in partnership with approved banks in Saudi Arabia. Our team can help you choose the best plan.",
    "faq.2.q": "Where are Sohar's projects located?",
    "faq.2.a": "Our projects are located in Riyadh's finest districts such as Al Narjis and Al Qairawan — strategic locations close to essential services and major roads.",
    "faq.3.q": "What are the expected delivery timelines?",
    "faq.3.a": "We commit to clear delivery schedules for each project. The expected delivery date is communicated at the time of contract, and we ensure adherence to agreed timelines.",
    "faq.4.q": "Do you offer warranties on residential units?",
    "faq.4.a": "Yes, we offer comprehensive warranties covering waterproofing, plumbing, electrical, sanitary fixtures, woodwork, and elevators. Warranty details are specified in the sales contract.",
    "faq.5.q": "Can I visit the projects before purchasing?",
    "faq.5.a": "Absolutely! We welcome visits to our projects at any time. Contact us to schedule an appointment and one of our representatives will guide you through a comprehensive tour.",
    "faq.6.q": "Are the units suitable for investment?",
    "faq.6.a": "Yes, our projects are in high-demand locations ensuring excellent investment returns through rental or resale. Many of our clients are investors who have achieved outstanding returns.",

    // Common project page
    "common.backHome": "Back to Home",
    "common.sohar": "\n",
    "common.callUs": "Call Us",
    "common.whatsapp": "WhatsApp",
    "common.luxuryProject": "Luxury Residential Project",
    "common.homeComfort": "Your Home... Your Comfort",
    "common.footerRights": "All rights reserved © 2026 Sohar Real Estate",
    "common.privacy": "Privacy Policy",
    "common.terms": "Terms & Conditions",

    // Adwar Project
    "adwar.title": "Narjis Floors",
    "adwar.subtitle": "27 luxury residential floors of various sizes in Al Narjis — Your Home... Your Comfort",
    "adwar.available": "Available for Sale",
    "adwar.unitsInfo": "9 Villas • 27 Separate Floors • 3 Models",
    "adwar.stat1": "Separate Floors",
    "adwar.stat2": "Residential Villas",
    "adwar.stat3": "Residential Models",
    "adwar.stat4": "m² Area Range",
    "adwar.aboutLabel": "About the Project",
    "adwar.aboutTitle": "Luxury You Deserve",
    "adwar.aboutP1": "Narjis Floors project consists of 27 separate residential floors, each featuring a unique architectural design with a unified visual identity, private entrance, and designated parking. It also includes separate electricity and water meters, providing complete privacy for residents.",
    "adwar.aboutP2": "The residential units have been meticulously designed to blend modern elegance with practical functionality. The use of premium materials such as marble, natural wood, and calm colors creates a warm atmosphere reflecting a sophisticated lifestyle.",
    "adwar.locationLabel": "Strategic Location",
    "adwar.locationTitle": "Al Narjis — Heart of Riyadh",
    "adwar.featuresLabel": "Project Features",
    "adwar.featuresTitle": "Details That Make the Difference",
    "adwar.floorLabel": "Unit Models",
    "adwar.floorTitle": "Floor Plans",
    "adwar.guaranteesLabel": "Project Guarantees",
    "adwar.guaranteesTitle": "Guarantees",
    "adwar.designLabel": "Interior Design",
    "adwar.designTitle": "Masterful Designs",
    "adwar.designDesc": "The residential units have been meticulously designed to blend modern elegance with practical functionality. The use of premium materials such as marble and natural wood, combined with calm colors, creates a warm atmosphere reflecting a sophisticated lifestyle.",
    "adwar.galleryLabel": "Photo Gallery",
    "adwar.galleryTitle": "Glimpses of the Project",
    "adwar.ctaDesc": "Contact us now to reserve your unit in Narjis Floors project",
    "adwar.feat.electricity": "Independent Electricity",
    "adwar.feat.water": "Separate Water Meters",
    "adwar.feat.security": "Security Cameras",
    "adwar.feat.ac": "Concealed Central AC",
    "adwar.feat.soundproof": "Sound Insulation Between Floors",
    "adwar.feat.parking": "Private Parking",
    "adwar.feat.entrance": "Separate Entrances",
    "adwar.feat.finishes": "Luxury Finishes",
    "adwar.guarantee.waterproof": "Waterproofing & Thermal Insulation",
    "adwar.guarantee.plumbing": "Plumbing",
    "adwar.guarantee.electrical": "Electrical Wiring",
    "adwar.guarantee.sanitary": "Sanitary Fixtures",
    "adwar.guarantee.woodwork": "Woodwork",
    "adwar.guarantee.meters": "Meters",
    "adwar.guarantee.elevators": "Elevators",
    "adwar.floor.ground": "Ground Floor",
    "adwar.floor.first": "First Floor",
    "adwar.floor.second": "Second Floor",
    "adwar.dist.kingdom": "Kingdom Hospital",
    "adwar.dist.kingdom.time": "3 min",
    "adwar.dist.sabic": "SABIC Train Station",
    "adwar.dist.sabic.time": "7 min",
    "adwar.dist.park": "Neighborhood Park & Mosque",
    "adwar.dist.park.time": "2 min",
    "adwar.dist.imam": "Imam University",
    "adwar.dist.imam.time": "5 min",
    "adwar.dist.stadium": "King Salman Stadium",
    "adwar.dist.stadium.time": "5 min",
    "adwar.dist.expo": "Expo 2030",
    "adwar.dist.expo.time": "8 min",
    "adwar.dist.airport": "King Khalid Airport",
    "adwar.dist.airport.time": "15 min",
    "adwar.dist.roshn": "Roshn Waterfront",
    "adwar.dist.roshn.time": "10 min",

    // Narjis Project
    "narjis.title": "Al Narjis Villas",
    "narjis.subtitle": "One of Sohar's residential projects — 6 luxury villas with areas of 207-230 m²",
    "narjis.available": "Available for Sale",
    "narjis.unitsInfo": "6 Villas • Areas 207-230 m²",
    "narjis.stat1": "Luxury Villas",
    "narjis.stat2": "Different Models",
    "narjis.stat3": "m² Area Range",
    "narjis.stat4": "Floors per Villa",
    "narjis.aboutLabel": "About the Project",
    "narjis.aboutTitle": "Al Narjis Villas",
    "narjis.aboutP1": "One of Sohar's residential projects, consisting of 6 luxury villas with different areas ranging from 207 to 230 m², with various specifications to meet all customer needs and requirements.",
    "narjis.aboutP2": "At Sohar, we believe that a person's safety and happiness in their home stems from an inner feeling of comfort and tranquility when seeing housing options that match their desires and aspirations.",
    "narjis.featuresLabel": "Project Specifications",
    "narjis.featuresTitle": "Villa Specifications",
    "narjis.feat.ac": "Concealed AC",
    "narjis.feat.insulation": "High-Quality Insulation",
    "narjis.feat.meters": "Independent Water & Electricity Meters",
    "narjis.feat.elevator": "Elevator Foundation",
    "narjis.feat.tanks": "Water Tanks",
    "narjis.modelsLabel": "Villa Details",
    "narjis.modelsTitle": "3 Residential Models",
    "narjis.model": "Model",
    "narjis.groundFloor": "Ground Floor",
    "narjis.firstFloor": "First Floor",
    "narjis.secondFloor": "Second Floor",
    "narjis.locationLabel": "Project Location",
    "narjis.locationTitle": "Al Narjis — Riyadh",
    "narjis.nearby.hospitals": "Hospitals",
    "narjis.nearby.malls": "Shopping Malls",
    "narjis.nearby.universities": "Universities",
    "narjis.nearby.gyms": "Sports Clubs",
    "narjis.roadsNote": "Less than 3 minutes from",
    "narjis.road1": "Abu Bakr Al-Siddiq Road",
    "narjis.road2": "Uthman ibn Affan Road",
    "narjis.road3": "Al-Thumamah Road",
    "narjis.designLabel": "Architectural Design",
    "narjis.designTitle": "Modern Luxury Designs",
    "narjis.designP1": "Al Narjis villas feature modern architectural designs that combine elegance and practicality. Spacious, open areas with high-quality building materials and luxury finishes ensure a comfortable and distinctive residential environment.",
    "narjis.designP2": "Each villa is carefully designed to provide maximum privacy and comfort, with attention to natural ventilation and ideal lighting in all rooms.",
    "narjis.ctaDesc": "Contact us now to reserve your villa",

    // Qairawan Project
    "qairawan.title": "Al Qairawan Villas",
    "qairawan.subtitle": "Sohar Real Estate villas in Al Qairawan — building areas up to 570 m² with modern luxury designs",
    "qairawan.status": "Coming Soon",
    "qairawan.unitsInfo": "2 Models • Areas 483-570 m²",
    "qairawan.stat1": "Residential Models",
    "qairawan.stat2": "Floors per Villa",
    "qairawan.stat3": "m² Building Area",
    "qairawan.stat4.value": "Al Qairawan",
    "qairawan.stat4": "District",
    "qairawan.aboutLabel": "About the Project",
    "qairawan.aboutTitle": "Al Qairawan Villas",
    "qairawan.aboutP1": "Sohar Real Estate villas are located in Al Qairawan district on a 280 sqm plot on a wide 20m street. In collaboration with architect Mansour Janoubi, exceptional designs were created combining luxury and practicality.",
    "qairawan.aboutP2": "The project is located near King Salman Road in Al Dar scheme, next to the largest modern downtown in the world, making it a distinguished investment location.",
    "qairawan.featuresLabel": "Comfortable & Integrated Living Experience",
    "qairawan.featuresTitle": "Villa Specifications",
    "qairawan.feat.ac": "Concealed AC for Entire Villa",
    "qairawan.feat.electricity": "Suspended Electrical Wiring",
    "qairawan.feat.aluminum": "Dritmal Aluminum",
    "qairawan.feat.plumbing": "Suspended Supply Plumbing",
    "qairawan.feat.flooring": "Spanish Porcelain Flooring",
    "qairawan.feat.elevator": "Complete Elevator Foundations",
    "qairawan.interiorLabel": "Architectural Design",
    "qairawan.interiorTitle": "Modern Luxury Designs",
    "qairawan.interiorP1": "In collaboration with architect Mansour Janoubi and Anos Architectural Design Office, the villas were designed in a modern style combining comfort and elegance with attention to the finest details.",
    "qairawan.interiorP2": "We craft a comfortable and integrated living experience with the highest standards and specifications.",
    "qairawan.modelsLabel": "Villa Details",
    "qairawan.modelsTitle": "Two Residential Models",
    "qairawan.buildArea": "Building Area",
    "qairawan.modelA": "Model A",
    "qairawan.modelB": "Model B",
    "qairawan.groundFloor": "Ground Floor",
    "qairawan.firstFloor": "First Floor",
    "qairawan.secondFloor": "Second Floor (Annex)",
    "qairawan.room.majlis": "Majlis",
    "qairawan.room.dining": "Dining Hall",
    "qairawan.room.familyHall": "Family Hall",
    "qairawan.room.library": "Library Space",
    "qairawan.room.familyLiving": "Family Living Room",
    "qairawan.room.laundry": "Laundry Room",
    "qairawan.room.maid": "Maid's Room",
    "qairawan.room.parking": "Car Parking",
    "qairawan.room.kitchen": "Kitchen",
    "qairawan.room.storage": "Storage",
    "qairawan.room.courtyard": "Inner Courtyard",
    "qairawan.room.outdoor": "Outdoor Seating",
    "qairawan.room.master": "Master Room",
    "qairawan.room.masterSuite": "Master Suite",
    "qairawan.room.bedrooms": "Two Bedrooms",
    "qairawan.room.balcony": "Balcony",
    "qairawan.locationLabel": "Project Location",
    "qairawan.locationTitle": "Al Qairawan — Riyadh",
    "qairawan.nearby.downtown": "Next to the Largest Modern Downtown in the World",
    "qairawan.nearby.malls": "Shopping Malls",
    "qairawan.nearby.schools": "Universities & Schools",
    "qairawan.nearby.gyms": "Sports Clubs",
    "qairawan.locationNote": "Location",
    "qairawan.locationDesc": "Al Qairawan District, near King Salman Road — Al Dar Scheme — Riyadh",
    "qairawan.ctaDesc": "Contact us now to inquire about the project",
  },
};

const I18nContext = createContext<I18nContextType | null>(null);

export const I18nProvider = ({ children, initialLang = "ar" }: { children: ReactNode; initialLang?: Lang }) => {
  const [lang, setLang] = useState<Lang>(initialLang);

  const t = (key: string): string => {
    return translations[lang]?.[key] || translations.ar[key] || key;
  };

  const isRTL = lang === "ar";

  return (
    <I18nContext.Provider value={{ lang, setLang, t, isRTL }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within I18nProvider");
  return context;
};
